// OwnerTabs.tsx
import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography
} from '@mui/material';
import Owner from './Owner';
import { Owner as OwnerType } from 'form-application-shared';

interface OwnerTabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  producerIndex: number;
}

// Helper component for the owner tab panels
function OwnerTabPanel(props: OwnerTabPanelProps) {
  const { children, value, index, producerIndex, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`owner-tabpanel-${producerIndex}-${index}`}
      aria-labelledby={`owner-tab-${producerIndex}-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface OwnerTabsProps {
  producerIndex: number;
  owners: OwnerType[];
  addOwner: (producerIndex: number) => void;
  removeOwner: (producerIndex: number, ownerIndex: number) => void;
  handleOwnerChange: (producerIndex: number, ownerIndex: number, field: keyof OwnerType, value: string | number) => void;
  validationMode: boolean;
  fieldErrors: Record<number, any> | undefined;
}

const OwnerTabs: React.FC<OwnerTabsProps> = ({ 
  producerIndex, 
  owners, 
  addOwner, 
  removeOwner, 
  handleOwnerChange, 
  validationMode, 
  fieldErrors 
}) => {
  const [activeOwnerTab, setActiveOwnerTab] = useState<number>(0);

  useEffect(() => {
    if (activeOwnerTab >= owners.length && owners.length > 0) {
      setActiveOwnerTab(owners.length - 1);
    }
  }, [owners.length, activeOwnerTab]);

  const handleOwnerTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === owners.length) {
      // "Add Owner" tab clicked: add a new owner
      addOwner(producerIndex);
      setActiveOwnerTab(newValue);
    } else {
      setActiveOwnerTab(newValue);
    }
  };

  // Check if any owner field has an error
  const ownerHasError = (ownerIndex: number): boolean => {
    if (!validationMode || !fieldErrors?.[producerIndex]?.owners?.[ownerIndex]) return false;
    return Object.values(fieldErrors[producerIndex].owners[ownerIndex]).some(error => error);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Owners for Producer {producerIndex + 1}
      </Typography>
      <Tabs
        value={activeOwnerTab}
        onChange={handleOwnerTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {owners.map((_, idx) => (
          <Tab 
            key={idx} 
            label={`Owner ${idx + 1}`}
            sx={ownerHasError(idx) ? { 
              color: 'error.main',
              '&::after': {
                content: '"*"',
                color: 'error.main',
                marginLeft: '2px'
              }
            } : {}}
          />
        ))}
        <Tab label="Add Owner" />
      </Tabs>
      {owners.map((owner, idx) => (
        <OwnerTabPanel value={activeOwnerTab} index={idx} key={idx} producerIndex={producerIndex}>
          <Owner 
            owner={owner}
            producerIndex={producerIndex}
            ownerIndex={idx}
            handleOwnerChange={handleOwnerChange}
            removeOwner={removeOwner}
            validationMode={validationMode}
            fieldErrors={fieldErrors}
            canDelete={owners.length > 1}
          />
        </OwnerTabPanel>
      ))}
    </Box>
  );
}

export default OwnerTabs;